import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { getBaseApiUrl } from './EnvironmentManager';

export const UserRoles = {
  SUPER_ADMIN: 'super-admin',
  FRIENDS_FAMILY: 'friends-family',
  SUPPORT: 'support',
  CREATOR: 'creator',
} as const;

export type UserRole = typeof UserRoles[keyof typeof UserRoles];

export enum UserEventType {
  AI_GENERATION = 'ai-generation',
  PRODUCT_CLICK = 'product-click',
  PRODUCT_ORDERED = 'product-ordered'
}

export type UserEventFrequencies = 'daily' | 'weekly' | 'monthly' | 'alltime';

export interface UsageSummary {
  used: number;
  limit?: number;
  remaining?: number;
  resetDate?: string;
}

export type UsageByFrequency = Record<UserEventFrequencies, UsageSummary>;

export type UserUsage = {
  [eventType in UserEventType]?: UsageByFrequency;
};

interface UserRequestParams {
  userId: string;
  email: string;
  name?: string;
}

const getUserApiUrl = () => {
  return `${getBaseApiUrl()}/user`;
};

export interface User {
  userId: string;
  email: string;
  name?: string;
  roles?: string[];
  createdDate?: Date;
  updatedDate?: Date;
  loginProvider?: string;
  creditPool?: number;

  /** Transient usage info for frontend use */
  usage?: UserUsage;
}

const useUserApi = () => {
  const { getAccessTokenSilently, user } = useAuth0();

  const getCurrentUser = async (): Promise<User | null> => {
    // No Auth0 user? Assume it's a public page
    if (!user || !user.sub) {
      return null;
    }

    // Check session cache
    const cached = sessionStorage.getItem('currentUser');
    if (cached) {
      try {
        return JSON.parse(cached) as User;
      } catch {
        sessionStorage.removeItem('currentUser');
      }
    }

    // Fetch from your API
    try {
      const fetchedUser = await getUser(user.sub);
      sessionStorage.setItem('currentUser', JSON.stringify(fetchedUser));
      return fetchedUser;
    } catch (err) {
      console.error("Failed to fetch user from backend:", err);
      return null;
    }
  };

  const createUser = async (params: UserRequestParams): Promise<User> => {
    const endpointUrl = getUserApiUrl();
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.post(endpointUrl, params, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response?.status === 200 && response?.data?.status) {
        return response.data.data;
      } else {
        throw new Error('Unable to create user.');
      }
    } catch (error) {
      console.log('Error creating user:', JSON.stringify(error));
      throw new Error('Failed to create user');
    }
  };

  const updateUser = async (params: UserRequestParams): Promise<User> => {
    const endpointUrl = getUserApiUrl();
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.put(endpointUrl, params, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response?.status === 200 && response?.data?.status) {
        // Clear cache to force fresh fetch next time
        sessionStorage.removeItem('currentUser');
        return response.data.data;
      } else {
        throw new Error('Unable to update user.');
      }
    } catch (error) {
      console.log('Error updating user:', JSON.stringify(error));
      throw new Error('Failed to update user');
    }
  };

  const getUsers = async (
    limit: number = 10,
    lastKey?: Record<string, any>
  ): Promise<{ items: Array<Object>; lastKey: Record<string, any> | null }> => {
    const params = new URLSearchParams({
      limit: limit.toString(),
      lastKey: lastKey ? JSON.stringify(lastKey) : '',
    });

    const endpointUrl = `${getUserApiUrl()}?${params.toString()}`;
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.get(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (
        response?.status === 200 &&
        response?.data?.status === 'success' &&
        response?.data?.data
      ) {
        return response.data.data;
      } else {
        throw new Error(
          'No user data found in the response or status is not success.'
        );
      }
    } catch (error) {
      console.error('Error calling user API:', JSON.stringify(error));
      throw new Error('Failed to retrieve user data from the API');
    }
  };

  const getUser = async (userId: string): Promise<User> => {
    const endpointUrl = `${getUserApiUrl()}/${encodeURIComponent(userId)}`;
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.get(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response?.status === 200 && response?.data?.data) {
        response.data.data.loginProvider = getLoginProvider(response?.data?.data.userId)
        return response.data.data as User;
      } else {
        throw new Error(
          'No user data found in the response or status is not success.'
        );
      }
    } catch (error: any) {
      console.error('Error calling user API:', error?.message);
      throw new Error('Failed to retrieve user data from the API');
    }
  };

  const getOrCreateUser = async (): Promise<User> => {
    const auth0Id = user?.sub;
    const email = user?.email;

    if (!auth0Id || !email) {
      throw new Error('Authenticated user information is missing.');
    }

    try {
      const existingUser = await getUser(auth0Id);
      if (existingUser) {
        // Check if user is missing email or name and update if needed
        const needsUpdate = false; //TODO: FIX THIS !existingUser.email || !existingUser.name;

        if (needsUpdate) {
          console.log('Existing user missing data, updating...', {
            hasEmail: !!existingUser.email,
            hasName: !!existingUser.name
          });

          // Extract name from Auth0 (same logic as create)
          let userName = user?.name;
          if (!userName && (user?.given_name || user?.family_name)) {
            userName = [user?.given_name, user?.family_name].filter(Boolean).join(' ').trim();
          }
          if (!userName) {
            userName = user?.nickname || email?.split('@')[0];
          }

          // Update the user with missing fields
          const updatedUser = await updateUser({
            userId: auth0Id,
            email: existingUser.email || email,
            name: existingUser.name || userName,
          });

          return updatedUser;
        }

        return existingUser;
      }
    } catch (error) {
      // Log unexpected errors only — not "not found"
      console.error('Unexpected error fetching user:', error);
      throw new Error('Error fetching user.');
    }

    // If no user found, create one
    console.info('User not found. Creating a new user...');

    // Extract name from Auth0 user object
    // For Google OAuth: given_name, family_name, or name
    // For other providers: name, nickname, or email fallback
    let userName = user?.name;
    if (!userName && (user?.given_name || user?.family_name)) {
      // Construct name from given_name and family_name (Google OAuth format)
      userName = [user?.given_name, user?.family_name].filter(Boolean).join(' ').trim();
    }
    if (!userName) {
      // Fallback to nickname or email prefix
      userName = user?.nickname || email?.split('@')[0];
    }

    console.log('Creating user with:', { userId: auth0Id, email, name: userName });

    const newUser = await createUser({
      userId: auth0Id,
      email,
      name: userName,
    });

    return newUser;
  };

  function hasRole(user: User, role: string): boolean {
    return user?.roles?.includes(role) ?? false;
  }

  function hasAnyRole(user: User, rolesToCheck: string[]): boolean {
    if (!user?.roles || user?.roles.length === 0) return false;
    return rolesToCheck.some(role => user?.roles!.includes(role));
  }

  return { createUser, updateUser, getUsers, getUser, getCurrentUser, getOrCreateUser, hasRole, hasAnyRole };
};

function getLoginProvider(sub?: string): string {
  if (!sub) return "unknown";
  const provider = sub.split("|")[0];
  switch (provider) {
    case "google-oauth2":
      return "Google";
    case "facebook":
      return "Facebook";
    case "auth0":
      return "Email/Password";
    default:
      return provider; // fallback for custom or enterprise connections
  }
};


export default useUserApi;
